import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import { AddAccount } from "app/pages/accounts/add-account/AddAccount";
import AccountsPage from "app/pages/accounts/AccountsPage";
import TransactionInvoicesTablePage from "app/pages/accounts/transactionInvoicesTable/TransactionInvoicesTablePage";
import ClientTransactionsTablePage from "app/pages/accounts/clientTransactionsTable/ClientTransactionsTablePage";
import CreditControlStatusPage from "app/modules/creditControl/CreditControlStatusPage";
import CreditControlUnitPage from "app/modules/creditControl/CreditControlUnitPage";
import GlobalSearchResultsPage from "app/modules/shared/globalSearch/GlobalSearchResultPage";
import DocumentInstancesPage from "app/modules/documents/DocumentInstancesPage";
import DevelopmentNewsPage from "app/modules/developments/news/DevelopmentNewsPage";
import DashboardPage from "app/pages/dashboard/DashboardPage";
import DevelopmentNewsPostPage from "app/modules/developments/news/DevelopmentNewsPostPage";
import { AccountPage } from "app/modules/account/AccountPage";
import { ContactDetails } from "app/modules/account/components/cards/ClientContactDetails/ContactDetails";
import { useSelector } from "react-redux";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import ClientBudgetsTablePage from "app/modules/budgets/ClientBudgetsTablePage";
import ClientBudgetHeadersTablePage from "app/modules/budgets/ClientBudgetHeadersTablePage";
import ClientBudgetHeaderChargesTablePage from "app/modules/budgets/ClientBudgetHeaderChargesTablePage";

const PrivateRoutes = () => {
  const globalSettings = useSelector(selectGlobalSettings);

  const JobsTablePage = lazy(() => import("app/modules/jobs/JobsTablePage"));
  const JobDetailsPage = lazy(() => import("app/modules/jobs/JobDetailsPage"));
  const CasesTablePage = lazy(() => import("app/modules/cases/CasesTablePage"));
  const CaseDetailsPage = lazy(() => import("app/modules/cases/CaseDetailsPage"));
  const ProposedWorksTablePage = lazy(() => import("app/modules/proposedWorks/ProposedWorksTablePage"));
  const ProposedWorksDetailsPage = lazy(() => import("app/modules/proposedWorks/ProposedWorksDetailsPage"));
  const DocumentsTablePage = lazy(() => import("app/modules/documents/DocumentsTablePage"));
  const DocumentAttachmentsPage = lazy(() => import("app/modules/documents/DocumentAttachmentsPage"));
  const CreditControlPage = lazy(() => import("app/modules/creditControl/CreditControlDevelopmentPage"));
  const PayNowPage = lazy(() => import("app/modules/payments/PayNowPage"));
  const MeetingsTablePage = lazy(() => import("app/modules/meetings/MeetingsTablePage"));
  const MeetingDetailsPage = lazy(() => import("app/modules/meetings/MeetingDetailsPage"));
  const DevelopmentInfoPage = lazy(() => import("app/modules/developments/DevelopmentInfoPage"));

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />
        {/* Pages */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="ClientTransactions/:clientID/*" element={<ClientTransactionsTablePage />} />
        <Route path="ClientTransactions/:clientID/:transactionID/*" element={<TransactionInvoicesTablePage />} />
        <Route path="addaccount" element={<AddAccount />} />
        {/* Lazy Modules */}
        {globalSettings.showBudgets && (
          <>
            <Route path="ClientBudgets/:clientID/*" element={<ClientBudgetsTablePage />} />
            <Route path="ClientBudgets/:clientID/:budgetID/*" element={<ClientBudgetHeadersTablePage />} />
            <Route
              path="ClientBudgets/:clientID/:budgetID/:budgetHeaderID/*"
              element={<ClientBudgetHeaderChargesTablePage />}
            />
          </>
        )}
        {globalSettings.showJobs && (
          <>
            <Route
              path="Jobs"
              element={
                <SuspensedView>
                  <JobsTablePage />
                </SuspensedView>
              }
            />
            <Route
              path="Job/:jobID/:taskSeqNo/*"
              element={
                <SuspensedView>
                  <JobDetailsPage />
                </SuspensedView>
              }
            />
            <Route
              path="Job/:estimateRequestID/*"
              element={
                <SuspensedView>
                  <JobDetailsPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showCases && (
          <>
            <Route
              path="Cases"
              element={
                <SuspensedView>
                  <CasesTablePage />
                </SuspensedView>
              }
            />
            <Route
              path="Case/:caseID/*"
              element={
                <SuspensedView>
                  <CaseDetailsPage />
                </SuspensedView>
              }
            />
            <Route
              path="Case/"
              element={
                <SuspensedView>
                  <CaseDetailsPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showProposedWorks && (
          <>
            <Route
              path="majorworks"
              element={
                <SuspensedView>
                  <ProposedWorksTablePage />
                </SuspensedView>
              }
            />
            <Route
              path="majorwork/:proposedWorksID/*"
              element={
                <SuspensedView>
                  <ProposedWorksDetailsPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showDocuments && (
          <>
            <Route
              path="Documents"
              element={
                <SuspensedView>
                  <DocumentsTablePage />
                </SuspensedView>
              }
            />
            <Route
              path="DocumentAttachments/:documentID/*"
              element={
                <SuspensedView>
                  <DocumentAttachmentsPage />
                </SuspensedView>
              }
            />
            <Route
              path="DocumentInstances/:documentID/:propertyAddress/*"
              element={
                <SuspensedView>
                  <DocumentInstancesPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showCreditControl && (
          <>
            <Route
              path="CreditControl"
              element={
                <SuspensedView>
                  <CreditControlPage />
                </SuspensedView>
              }
            />
            <Route
              path="CreditControl/:developmentID/*"
              element={
                <SuspensedView>
                  <CreditControlStatusPage />
                </SuspensedView>
              }
            />
            <Route
              path="CreditControl/:developmentID/:statusID/*"
              element={
                <SuspensedView>
                  <CreditControlUnitPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showPayments && (
          <>
            <Route
              path="Payments/PayNow"
              element={
                <SuspensedView>
                  <PayNowPage />
                </SuspensedView>
              }
            />
            {globalSettings.showProposedWorks && (
              <>
                <Route
                  path="Payments/PayNow/:proposedWorkID"
                  element={
                    <SuspensedView>
                      <PayNowPage />
                    </SuspensedView>
                  }
                />
              </>
            )}
          </>
        )}
        {globalSettings.showMeetings && (
          <>
            <Route
              path="Meetings"
              element={
                <SuspensedView>
                  <MeetingsTablePage />
                </SuspensedView>
              }
            />
            <Route
              path="Meeting/:meetingID/*"
              element={
                <SuspensedView>
                  <MeetingDetailsPage />
                </SuspensedView>
              }
            />
          </>
        )}
        <Route
          path="SearchResults/"
          element={
            <SuspensedView>
              <GlobalSearchResultsPage />
            </SuspensedView>
          }
        />
        {globalSettings.showDevelopmentNews && (
          <>
            <Route
              path="News/*"
              element={
                <SuspensedView>
                  <DevelopmentNewsPage />
                </SuspensedView>
              }
            />
            <Route
              path="News/:developmentNewsID/*"
              element={
                <SuspensedView>
                  <DevelopmentNewsPostPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {globalSettings.showDevelopmentInfo && (
          <>
            <Route
              path="DevelopmentInfo/*"
              element={
                <SuspensedView>
                  <DevelopmentInfoPage />
                </SuspensedView>
              }
            />
          </>
        )}
        <Route
          path="account/*"
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path="contactDetails/:accountNumber"
          element={
            <SuspensedView>
              <ContactDetails />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
